import React, { useState, useMemo } from 'react';
import {
  Search,
  Plus,
  Sparkles,
  ChevronDown,
  ChevronRight,
  LayoutGrid,
} from 'lucide-react';
import type { BlockNode } from '../types';
import { BLOCK_MANIFESTS, type BlockManifest } from '../blocks/generated/manifest';
import { SECTION_PRESETS, type SectionPreset } from '../templates/sectionPresets';
import { BlockWireframeThumbnail } from './BlockWireframeThumbnails';

export interface BlockLibraryRailProps {
  onInsertBlock: (blockType: string, category: BlockNode['category']) => void;
  onInsertSection?: (section: SectionPreset) => void;
}

interface CategoryGroup {
  id: string;
  name: string;
  isSectionGroup?: boolean;
  types: string[];
}

const CATEGORY_GROUPS: CategoryGroup[] = [
  {
    id: 'turnkey_sections',
    name: 'Ready-Made Sections',
    isSectionGroup: true,
    types: [],
  },
  {
    id: 'hero_marketing',
    name: 'Hero & Banners',
    types: ['HeroSplit', 'CallToActionBanner', 'StatsCounter', 'CountdownTimer', 'VideoPlayer'],
  },
  {
    id: 'academics_toppers',
    name: 'Coaching, Toppers & Faculty',
    types: ['ToppersShowcase', 'TopperCard', 'FacultyShowcase', 'Testimonials', 'AccordionFAQ', 'SchoolNoticeBoardBlock'],
  },
  {
    id: 'erp_courses',
    name: 'Courses & Live ERP Slots',
    types: [
      'CoachCourseCatalogBlock',
      'AppProductCatalogBlock',
      'ConversionFormBlock',
      'AdmissionsStatusTrackerBlock',
      'FeePaymentPortalBlock',
    ],
  },
  {
    id: 'commerce_store',
    name: 'Pricing & Study Store',
    types: ['PricingTable', 'DigitalProductList', 'DigitalProductCard', 'StorefrontCheckoutBlock'],
  },
  {
    id: 'layout_structure',
    name: 'Layout & Containers',
    types: ['Section', 'Container', 'Grid2Col', 'Divider', 'Spacer'],
  },
  {
    id: 'typography_media',
    name: 'Text, Buttons & Media',
    types: ['Heading', 'Paragraph', 'RichTextBlock', 'Button', 'ImageGallery', 'MathFormulaBlock'],
  },
  {
    id: 'legal_compliance',
    name: 'Legal & Compliance',
    types: ['LegalAgreementBlock', 'ConsentCheckboxBlock', 'CookieBannerBlock'],
  },
];

export function BlockLibraryRail({
  onInsertBlock,
  onInsertSection,
}: BlockLibraryRailProps): React.JSX.Element {
  const [searchQuery, setSearchQuery] = useState('');
  // Empty object = all categories collapsed by default (Requirement 8)
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  const toggleCategory = (catId: string) => {
    setExpandedCategories((prev) => ({ ...prev, [catId]: !prev[catId] }));
  };

  // Filter block types based on search
  const filteredManifests = useMemo<Record<string, BlockManifest>>(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return BLOCK_MANIFESTS;
    const res: Record<string, BlockManifest> = {};
    for (const [k, m] of Object.entries(BLOCK_MANIFESTS)) {
      if (
        k.toLowerCase().includes(q) ||
        m.title?.toLowerCase().includes(q) ||
        m.description?.toLowerCase().includes(q) ||
        m.category?.toLowerCase().includes(q)
      ) {
        res[k] = m;
      }
    }
    return res;
  }, [searchQuery]);

  const filteredSections = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return SECTION_PRESETS;
    return SECTION_PRESETS.filter(
      (sec) =>
        sec.title.toLowerCase().includes(q) ||
        sec.description.toLowerCase().includes(q) ||
        sec.category.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const isSearching = searchQuery.trim().length > 0;

  return (
    <div className="flex flex-col h-full bg-card border-r border-border text-foreground select-none w-60 shrink-0 overflow-hidden">
      {/* Search Input - Compact micro-typography */}
      <div className="p-2 border-b border-border bg-muted/20 shrink-0">
        <div className="relative flex items-center">
          <Search className="absolute left-2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            placeholder="Search blocks & sections..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-7 pr-2 py-1 h-7 text-[11px] rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
          />
        </div>
      </div>

      {/* Main Unified Content Area */}
      <div className="flex-1 overflow-y-auto p-1.5 space-y-1.5">
        {isSearching ? (
          <div className="space-y-2">
            {/* Search Results: Blocks */}
            {Object.keys(filteredManifests).length > 0 && (
              <div>
                <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-1 mb-1">
                  Components ({Object.keys(filteredManifests).length})
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {Object.keys(filteredManifests).map((bType) => {
                    const manifest = filteredManifests[bType];
                    const isErp = (manifest as any)?.erpSlot || manifest?.category === 'erp_binding';
                    return (
                      <div
                        key={bType}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData(
                            'application/json',
                            JSON.stringify({ kind: 'new_block', type: bType, category: manifest.category })
                          );
                          e.dataTransfer.effectAllowed = 'copy';
                        }}
                        onClick={() => onInsertBlock(bType, manifest.category)}
                        className="flex flex-col items-center justify-center p-1.5 rounded-md border border-border/80 bg-background hover:border-primary hover:shadow-xs transition-all cursor-pointer group text-center"
                        title={`Drag or click to insert ${manifest.title || bType}`}
                      >
                        <BlockWireframeThumbnail type={bType} className="w-full h-6 mb-1 text-foreground/80 group-hover:text-primary" />
                        <span className="text-[10px] leading-tight font-medium text-foreground truncate w-full group-hover:text-primary">
                          {manifest.title || bType}
                        </span>
                        {isErp && (
                          <span className="text-[8px] font-mono text-primary bg-primary/10 px-1 rounded mt-0.5">
                            ERP
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Search Results: Turnkey Sections */}
            {filteredSections.length > 0 && (
              <div className="pt-1">
                <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-1 mb-1">
                  Sections ({filteredSections.length})
                </div>
                <div className="space-y-1">
                  {filteredSections.map((sec) => (
                    <div
                      key={sec.id}
                      onClick={() => onInsertSection && onInsertSection(sec)}
                      className="p-1.5 rounded-md border border-border bg-background hover:border-primary transition-all cursor-pointer group flex flex-col gap-0.5"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold text-foreground group-hover:text-primary transition-colors truncate">
                          {sec.title}
                        </span>
                        <Plus className="h-3 w-3 text-muted-foreground group-hover:text-primary shrink-0" />
                      </div>
                      <p className="text-[10px] text-muted-foreground line-clamp-1">
                        {sec.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(filteredManifests).length === 0 && filteredSections.length === 0 && (
              <div className="text-center py-6 text-muted-foreground text-[11px]">
                No components or sections found.
              </div>
            )}
          </div>
        ) : (
          /* Unified Grouped Accordion Categories (All Collapsed By Default) */
          CATEGORY_GROUPS.map((grp) => {
            const isExpanded = expandedCategories[grp.id] === true;

            if (grp.isSectionGroup) {
              return (
                <div key={grp.id} className="shrink-0 rounded-md border border-border/70 bg-card/60">
                  <button
                    type="button"
                    onClick={() => toggleCategory(grp.id)}
                    className="flex w-full items-center justify-between py-1.5 px-2 bg-muted/40 hover:bg-muted/70 text-[11px] font-semibold text-foreground cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="h-3 w-3 text-amber-500" />
                      <span>{grp.name}</span>
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <span className="text-[9px] font-mono px-1 py-0.2 rounded bg-muted/80">{SECTION_PRESETS.length}</span>
                      {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="space-y-1.5 p-1.5 bg-background/50 border-t border-border/50">
                      {SECTION_PRESETS.map((sec) => (
                        <div
                          key={sec.id}
                          onClick={() => onInsertSection && onInsertSection(sec)}
                          className="p-1.5 rounded-md border border-border/80 bg-background hover:border-primary hover:shadow-xs transition-all cursor-pointer group flex flex-col gap-0.5"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] font-semibold text-foreground group-hover:text-primary transition-colors truncate">
                              {sec.title}
                            </span>
                            <span className="text-[8px] bg-muted px-1 py-0.2 rounded text-muted-foreground uppercase font-mono">
                              {sec.category}
                            </span>
                          </div>
                          <p className="text-[10px] text-muted-foreground line-clamp-1">
                            {sec.description}
                          </p>
                          <div className="flex items-center justify-between pt-0.5 text-[9px] text-muted-foreground">
                            <span>{sec.blocks?.length || 1} blocks</span>
                            <span className="text-primary font-semibold flex items-center gap-0.5">
                              <Plus className="h-2.5 w-2.5" /> Insert
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            // Normal atomic component category
            const availableTypes = grp.types.filter((t) => (BLOCK_MANIFESTS as any)[t]);
            if (availableTypes.length === 0) return null;

            return (
              <div key={grp.id} className="shrink-0 rounded-md border border-border/70 bg-card/60">
                <button
                  type="button"
                  onClick={() => toggleCategory(grp.id)}
                  className="flex w-full items-center justify-between py-1.5 px-2 bg-muted/40 hover:bg-muted/70 text-[11px] font-semibold text-foreground cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-1.5">
                    <LayoutGrid className="h-3 w-3 text-muted-foreground" />
                    <span>{grp.name}</span>
                  </div>
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <span className="text-[9px] font-mono px-1 py-0.2 rounded bg-muted/80">{availableTypes.length}</span>
                    {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="grid grid-cols-2 gap-1.5 p-1.5 bg-background/50 border-t border-border/50">
                    {availableTypes.map((bType) => {
                      const manifest = (BLOCK_MANIFESTS as Record<string, BlockManifest>)[bType];
                      const isErp = (manifest as any)?.erpSlot || manifest?.category === 'erp_binding';
                      return (
                        <div
                          key={bType}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData(
                              'application/json',
                              JSON.stringify({ kind: 'new_block', type: bType, category: manifest?.category || 'layout' })
                            );
                            e.dataTransfer.effectAllowed = 'copy';
                          }}
                          onClick={() => onInsertBlock(bType, manifest?.category || 'layout')}
                          className="flex flex-col items-center justify-center p-1.5 rounded-md border border-border/80 bg-background hover:border-primary hover:shadow-xs transition-all cursor-pointer group text-center"
                          title={`Drag or click to insert ${manifest?.title || bType}`}
                        >
                          <BlockWireframeThumbnail type={bType} className="w-full h-6 mb-1 text-foreground/80 group-hover:text-primary" />
                          <span className="text-[10px] leading-tight font-medium text-foreground truncate w-full group-hover:text-primary">
                            {manifest?.title || bType}
                          </span>
                          {isErp && (
                            <span className="text-[8px] font-mono text-primary bg-primary/10 px-1 rounded mt-0.5">
                              ERP
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
